import {
  Injectable,
  Injector,
} from '@angular/core';
import {
  Actions,
  Effect,
  ofType,
} from '@ngrx/effects';
import {
  WindowActionTypes,
  OpenWindow,
  CloseWindow,
  MinimizeWindow,
  ReopenWindow,
  SetWindowWidth,
  SetWindowHeight,
  SetWindowPositiion,
  WindowOpend,
} from './window.actions';
import { WindowService } from './window.service';
import {
  tap,
  map,
  filter,
} from 'rxjs/operators';
import {
  WINDOW_CONTAINER_CONTEXT,
  WINDOW_CONTEXT,
} from './tokens';
import { WindowContainerContext } from './window-context';
import { WindowTasksService } from './window-tasks.service';
import { DefaultWindowComponent } from './default-window/default-window.component';
import { v1 as uuid } from 'uuid';
import {
  DX_DOCUMENT_ID_TOKEN,
  DX_DOCUMENT_TOKEN,
} from '../tokens';

@Injectable()
export class WindowEffects {

  @Effect()
  public openWindow$ = this.actions$.pipe(
    ofType<OpenWindow<any, any>>(OpenWindow.TYPE),
    filter(action => !this.windowTasks.open.has(action.config.id)),
    map(action => {

      const id = action.config.id || uuid();

      const injector = Injector.create({
        parent:    action.config.injector || this.injector,
        providers: [
          {
            provide:  WINDOW_CONTAINER_CONTEXT,
            useValue: {
                        id,
                        component: action.config.component,
                        template:  action.config.template,
                        injector:  action.config.injector || this.injector
                      } as WindowContainerContext<any>
          },
          {
            provide: WINDOW_CONTEXT,
            useValue: action.config.data,
          },
          {
            provide:  DX_DOCUMENT_ID_TOKEN,
            useValue: action.config.data && action.config.data.id || null,
          },
          {
            provide:  DX_DOCUMENT_TOKEN,
            useValue: action.config.data || null,
          }
        ]
      });

      const windowRef = this.window.open({
        id,
        ...action.config,
        component: DefaultWindowComponent,
        injector
      });

      this.windowTasks.open.set(id, windowRef);

      return new WindowOpend(action.config, id, action.tracer);

    })
  );

  @Effect({ dispatch: false })
  public closeWindow$ = this.actions$.pipe(
    ofType<CloseWindow>(WindowActionTypes.CloseWindow),
    tap(action => {

      const windowRef = this.windowTasks.open.get(action.windowId);

      if (windowRef) {

        windowRef.close();

        this.windowTasks.open.delete(action.windowId);

      }

    })
  );

  @Effect({ dispatch: false })
  public minimize$ = this.actions$.pipe(
    ofType<MinimizeWindow>(WindowActionTypes.MinimizeWindow),
    tap(action => this.windowTasks.open.get(action.windowId).minimize())
  );

  @Effect({ dispatch: false })
  public reopen$ = this.actions$.pipe(
    ofType<ReopenWindow>(WindowActionTypes.ReopenWindow),
    tap(action => this.windowTasks.open.get(action.windowId).reopen())
  );

  @Effect({ dispatch: false })
  public setWidth$ = this.actions$.pipe(
    ofType<SetWindowWidth>(WindowActionTypes.SetWindowWidth),
    tap(action => this.windowTasks.open.get(action.windowId).setWidth(action.width))
  );

  @Effect({ dispatch: false })
  public setHeight$ = this.actions$.pipe(
    ofType<SetWindowHeight>(WindowActionTypes.SetWindowHeight),
    tap(action => this.windowTasks.open.get(action.windowId).setHeight(action.height))
  );

  @Effect({ dispatch: false })
  public setPos$ = this.actions$.pipe(
    ofType<SetWindowPositiion>(WindowActionTypes.SetWindowPositiion),
    tap(action => this.windowTasks.open.get(action.windowId).setPos(action.x, action.y))
  );

  constructor(
    public actions$: Actions,
    public window: WindowService,
    public injector: Injector,
    public windowTasks: WindowTasksService,
  ) {}

}
